import { Link } from 'react-router-dom'
import { Bot, MessageCircle, BookOpen, HardDrive, AlertTriangle, TrendingUp } from 'lucide-react'
import { useUsage } from '../../hooks/useBilling'

interface UsageCardProps {
  icon: React.ReactNode
  title: string
  current: number
  limit: number
  isUnlimited: boolean
  unit?: string
}

function UsageCard({ icon, title, current, limit, isUnlimited, unit = '' }: UsageCardProps) {
  const percent = isUnlimited ? 0 : Math.min(100, (current / limit) * 100)
  const isWarning = !isUnlimited && percent > 80
  const isCritical = !isUnlimited && percent >= 100

  const barColor = isCritical
    ? 'from-red-400 to-red-600'
    : isWarning
    ? 'from-amber-400 to-amber-600'
    : 'from-emerald-400 to-emerald-600'

  return (
    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-5 dark:border-zinc-700 dark:bg-zinc-800/50">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`rounded-xl p-2.5 ${isCritical ? 'bg-red-100 text-red-600 dark:bg-red-900/40 dark:text-red-400' : isWarning ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-400' : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/40 dark:text-emerald-400'}`}>
            {icon}
          </div>
          <h3 className="font-semibold text-zinc-900 dark:text-white">{title}</h3>
        </div>
        {isWarning && !isCritical && (
          <span className="flex items-center gap-1 text-xs font-medium text-amber-600 dark:text-amber-400">
            <AlertTriangle className="h-3 w-3" /> Proche de la limite
          </span>
        )}
        {isCritical && (
          <span className="flex items-center gap-1 text-xs font-medium text-red-600 dark:text-red-400">
            <AlertTriangle className="h-3 w-3" /> Limite atteinte
          </span>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-end justify-between">
          <div>
            <span className="text-3xl font-bold text-zinc-900 dark:text-white">{current}</span>
            {!isUnlimited && (
              <span className="ml-1 text-lg text-zinc-500">/ {limit}</span>
            )}
            {unit && <span className="ml-1 text-sm text-zinc-500">{unit}</span>}
          </div>
          {isUnlimited ? (
            <span className="rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
              ∞ Illimité
            </span>
          ) : (
            <span className="text-sm text-zinc-500">{Math.round(percent)}%</span>
          )}
        </div>

        {!isUnlimited && (
          <div className="h-3 w-full overflow-hidden rounded-full bg-zinc-200 dark:bg-zinc-700">
            <div
              className={`h-full bg-gradient-to-r ${barColor} transition-all duration-500`}
              style={{ width: `${percent}%` }}
            />
          </div>
        )}

        {!isUnlimited && (
          <p className="text-sm text-zinc-500">
            {limit - current > 0 
              ? `${limit - current} ${title.toLowerCase()} restant${limit - current > 1 ? 's' : ''}`
              : 'Aucune place disponible'}
          </p>
        )}
      </div>
    </div>
  )
}

export default function AccountUsage() {
  const { usage, loading, refetch } = useUsage(30000)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    )
  }

  if (!usage) {
    return (
      <div className="py-8 text-center">
        <p className="text-zinc-500">Impossible de charger les statistiques d'utilisation.</p>
        <button
          onClick={refetch}
          className="mt-4 rounded-full bg-emerald-500 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-600"
        >
          Réessayer
        </button>
      </div>
    )
  }

  const hasWarning = 
    (!usage.agents.is_unlimited && usage.agents.current / usage.agents.limit > 0.8) ||
    (!usage.conversations.is_unlimited && usage.conversations.current / usage.conversations.limit > 0.8) ||
    (!usage.knowledge_items.is_unlimited && usage.knowledge_items.current / usage.knowledge_items.limit > 0.8) ||
    (!usage.storage_mb.is_unlimited && usage.storage_mb.current / usage.storage_mb.limit > 0.8)

  return (
    <div className="space-y-6">
      {/* Warning Banner */}
      {hasWarning && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/60 dark:bg-amber-950/40">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <div>
              <h3 className="font-medium text-amber-800 dark:text-amber-200">
                Vous approchez de vos limites
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                Certaines de vos ressources sont proches de la limite. Pensez à passer à un plan supérieur pour continuer à utiliser toutes les fonctionnalités.
              </p>
              <Link
                to="/pricing"
                className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-amber-800 hover:text-amber-900 dark:text-amber-200 dark:hover:text-amber-100"
              >
                <TrendingUp className="h-4 w-4" /> Voir les plans supérieurs
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Usage Cards Grid */}
      <div className="grid gap-4 sm:grid-cols-2">
        <UsageCard
          icon={<Bot className="h-5 w-5" />}
          title="Agents"
          current={usage.agents.current}
          limit={usage.agents.limit}
          isUnlimited={usage.agents.is_unlimited}
        />
        
        <UsageCard
          icon={<MessageCircle className="h-5 w-5" />}
          title="Conversations"
          current={usage.conversations.current}
          limit={usage.conversations.limit}
          isUnlimited={usage.conversations.is_unlimited}
          unit="ce mois"
        />
        
        <UsageCard
          icon={<BookOpen className="h-5 w-5" />}
          title="Documents"
          current={usage.knowledge_items.current}
          limit={usage.knowledge_items.limit}
          isUnlimited={usage.knowledge_items.is_unlimited}
        />
        
        <UsageCard
          icon={<HardDrive className="h-5 w-5" />}
          title="Stockage"
          current={Math.round(usage.storage_mb.current)}
          limit={usage.storage_mb.limit}
          isUnlimited={usage.storage_mb.is_unlimited}
          unit="MB"
        />
      </div>

      {/* Upgrade CTA */}
      <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-teal-50 p-6 dark:border-emerald-900/60 dark:from-emerald-950/40 dark:to-teal-950/40">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">
              Besoin de plus de ressources ?
            </h3>
            <p className="mt-1 text-sm text-emerald-700 dark:text-emerald-300">
              Passez à un plan supérieur pour débloquer plus d'agents, de conversations et de stockage.
            </p>
          </div>
          <Link
            to="/pricing"
            className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600"
          >
            Voir les offres
          </Link>
        </div>
      </div>
    </div>
  )
}
